import React from 'react';
import Button from '../../components/ui/Button';
import Panel from '../../components/ui/Panel';
import ComponentShowcase from '../../components/ui/ComponentShowcase';

const ButtonPage: React.FC = () => {
  const buttonVariantsCode = `<Button variant="primary">Primary Button</Button>
<Button variant="secondary">Secondary Button</Button>
<Button variant="danger">Danger Button</Button>`;

  const buttonSizesCode = `<Button size="small">Small Button</Button>
<Button size="medium">Medium Button</Button>
<Button size="large">Large Button</Button>`;

  const buttonWidthCode = `<Button fullWidth>Full Width Button</Button>`;

  const buttonDisabledCode = `<Button disabled>Disabled Button</Button>`;

  const buttonActionCode = `<Button onClick={() => console.log('Button clicked!')}>
  Click Me
</Button>`;

  return (
    <div>
      <h1 id="overview" className="text-3xl text-osrs-gold font-bold uppercase mb-4">Button</h1>
      <p className="text-osrs-yellow mb-8">
        Interface buttons are raised stone with yellow text. Danger buttons keep the stone and use red text, as warning copy does in the client. Primary, secondary, and danger are the three variants.
      </p>

      <section id="examples" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Examples</h2>
        
        <ComponentShowcase
          id="variants"
          title="Button Variants"
          description="Buttons come in three variants: primary, secondary, and danger."
          component={
            <div className="flex flex-wrap gap-4">
              <Button variant="primary">Primary Button</Button>
              <Button variant="secondary">Secondary Button</Button>
              <Button variant="danger">Danger Button</Button>
            </div>
          }
          code={buttonVariantsCode}
          className="mb-10"
        />
        
        <ComponentShowcase
          id="sizes"
          title="Button Sizes"
          description="Buttons are available in three sizes: small, medium (default), and large."
          component={
            <div className="flex flex-wrap gap-4 items-center">
              <Button size="small">Small Button</Button>
              <Button size="medium">Medium Button</Button>
              <Button size="large">Large Button</Button>
            </div>
          }
          code={buttonSizesCode}
          className="mb-10"
        />
        
        <ComponentShowcase
          id="full-width"
          title="Full Width Button"
          description="Buttons can expand to fill their container width."
          component={
            <div className="w-full max-w-md">
              <Button fullWidth>Full Width Button</Button>
            </div>
          }
          code={buttonWidthCode}
          className="mb-10"
        />
        
        <ComponentShowcase
          id="disabled"
          title="Disabled Button"
          description="Buttons can be disabled to indicate that they are not interactive."
          component={
            <Button disabled>Disabled Button</Button>
          }
          code={buttonDisabledCode}
          className="mb-10"
        />
        
        <ComponentShowcase
          id="on-click"
          title="Button with onClick Handler"
          description="Buttons can have click handlers for interactivity."
          component={
            <Button onClick={() => alert('Button clicked!')}>Click Me</Button>
          }
          code={buttonActionCode}
          className="mb-10"
        />
      </section>
      
      <section id="usage-guidelines" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Usage Guidelines</h2>
        
        <Panel className="mb-6">
          <h3 id="when-to-use" className="text-xl text-osrs-gold font-bold mb-3">When to Use</h3>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use primary buttons for the main action on a page</li>
            <li>Use secondary buttons for alternative or secondary actions</li>
            <li>Use danger buttons for destructive actions that need caution</li>
            <li>Keep button text concise and action-oriented</li>
            <li>Use uppercase text to match OSRS style</li>
          </ul>
        </Panel>
        
        <Panel className="mb-6">
          <h3 id="accessibility" className="text-xl text-osrs-gold font-bold mb-3">Accessibility</h3>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Ensure buttons have sufficient contrast against their background</li>
            <li>Use the disabled attribute for non-interactive buttons</li>
            <li>Consider adding aria-label for buttons with icons or unclear purposes</li>
            <li>Maintain focus states for keyboard navigation</li>
          </ul>
        </Panel>
      </section>
      
      <section id="api-reference" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">API Reference</h2>
        
        <Panel>
          <table className="w-full text-osrs-yellow">
            <thead>
              <tr className="border-b border-osrs-border">
                <th className="text-left py-2 px-4 text-osrs-gold">Prop</th>
                <th className="text-left py-2 px-4 text-osrs-gold">Type</th>
                <th className="text-left py-2 px-4 text-osrs-gold">Default</th>
                <th className="text-left py-2 px-4 text-osrs-gold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-osrs-border">
                <td className="py-2 px-4">variant</td>
                <td className="py-2 px-4">
                  <code>'primary' | 'secondary' | 'danger'</code>
                </td>
                <td className="py-2 px-4">
                  <code>'primary'</code>
                </td>
                <td className="py-2 px-4">The visual style of the button</td>
              </tr>
              <tr className="border-b border-osrs-border">
                <td className="py-2 px-4">size</td>
                <td className="py-2 px-4">
                  <code>'small' | 'medium' | 'large'</code>
                </td>
                <td className="py-2 px-4">
                  <code>'medium'</code>
                </td>
                <td className="py-2 px-4">The size of the button</td>
              </tr>
              <tr className="border-b border-osrs-border">
                <td className="py-2 px-4">fullWidth</td>
                <td className="py-2 px-4">
                  <code>boolean</code>
                </td>
                <td className="py-2 px-4">
                  <code>false</code>
                </td>
                <td className="py-2 px-4">Whether the button should take up the full width of its container</td>
              </tr>
              <tr className="border-b border-osrs-border">
                <td className="py-2 px-4">disabled</td>
                <td className="py-2 px-4">
                  <code>boolean</code>
                </td>
                <td className="py-2 px-4">
                  <code>false</code>
                </td>
                <td className="py-2 px-4">Whether the button is disabled</td>
              </tr>
              <tr>
                <td className="py-2 px-4">onClick</td>
                <td className="py-2 px-4">
                  <code>function</code>
                </td>
                <td className="py-2 px-4">
                  <code>undefined</code>
                </td>
                <td className="py-2 px-4">Function called when the button is clicked</td>
              </tr>
            </tbody>
          </table>
        </Panel>
      </section>
    </div>
  );
};

export default ButtonPage; 